import { validationProblemsTag } from './apiValidationProblems';
import { apiSlice, TemplateOrCalculation } from './api';
import {
  ApiOperationType,
  CalculationConfigurationDeleteRequest,
  CalculationConfigurationRequest,
  CalculationConfigurationResponse as CalculationConfigurationResponseOriginal,
  EntityListResponse,
  EntityListTypeName,
  RegionFlowType,
} from '@ydistri/api-sdk';
import { CalculationsCollection, TemplatesCollection } from '../swagger/collections';
import { SignalConfigurationChanged } from '../signalr/signalrInterfaces';
import { signalrClient } from '../signalr/client';
import { isEnumValue } from '@ydistri/generic-utils';
import { getTags, transformStringToSkuListConfigurationFlagIds } from './apiLib';
import { getSelectedProjectShortName } from './apiHeader';
import {
  apiEntityLists,
  getDefaultEntityListsParams,
} from '../screens/ProjectAdministration/EntityListAdministration/apiEntityLists';

const { TAGS, TAGS_ARRAY } = getTags('globalConfiguration');

export type CalculationConfigurationResponseTransformed = Omit<
  CalculationConfigurationResponseOriginal,
  'skuListConfigurationFlagIds'
> & {
  skuListConfigurationFlagIds?: Record<string, true | undefined>;
};

export interface UpdateCalculationConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  request: CalculationConfigurationRequest;
}

export interface DeleteCalculationConfigurationPayload {
  templateOrCalculation: TemplateOrCalculation;
  request: CalculationConfigurationDeleteRequest;
}

export const apiGlobalConfiguration = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getCalculationConfiguration: builder.query<
        CalculationConfigurationResponseTransformed | undefined,
        TemplateOrCalculation
      >({
        queryFn: async arg => {
          const {
            data: { data: calculationConfiguration },
          } = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateCalculationConfiguration(arg.id)
            : CalculationsCollection.getCalculationConfigurations(arg.id));

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- may be null for manual and merged calculations
          if (calculationConfiguration) {
            const skuListConfigurationFlagIds = transformStringToSkuListConfigurationFlagIds(
              calculationConfiguration.skuListConfigurationFlagIds ?? '',
            );
            return { data: { ...calculationConfiguration, skuListConfigurationFlagIds } };
          } else {
            return { data: undefined };
          }
        },
        providesTags: [TAGS.calculationConfiguration],
        async onCacheEntryAdded({ type: objectType, id: selectedTemplateId }, api) {
          // The project this entry was created for, read before the first await: a cache entry survives
          // a project switch (and may finish loading after one), and a template id is not unique across
          // projects, so a signal must match the project the entry belongs to as well as the id.
          const project = getSelectedProjectShortName();
          try {
            await api.cacheDataLoaded;
            // The request-list prefetch is asynchronous; only the newest request-list notification may
            // write, or a slow prefetch would restore a list a later clear removed.
            let requestListGeneration = 0;
            const listener = (signal: SignalConfigurationChanged) => {
              void (async () => {
                // eslint-disable-next-line no-console -- we want the output here
                console.log('CONFIGURATION CHANGED', signal);
                // A template's signal patches a template's entry only: a calculation may share the id.
                if (
                  objectType !== 'Template' ||
                  selectedTemplateId !== signal.templateId ||
                  signal.projectSlug !== project
                ) {
                  return;
                }
                if (signal.entityType === 'CalculationConfiguration') {
                  if (signal.operationType === ApiOperationType.Patch) {
                    // The request-list field needs the entity lists loaded, which is asynchronous;
                    // every other field is applied at once, in notification order. Applying them after
                    // the wait let an older patch's fields land over a newer notification's.
                    const applyField = (
                      draft: NonNullable<Parameters<Parameters<typeof api.updateCachedData>[0]>[0]>,
                      field: string,
                      value: string | null | undefined,
                      loadedRequestLists: EntityListResponse[] | undefined,
                    ): void => {
                      // An absent value says nothing; only an explicit null clears.
                      if (value === undefined) return;
                      // A null value is a field cleared to nothing (an explicit null on the wire,
                      // never "unchanged"): the request list and the flags are cleared this way.
                      if (field in draft || field === 'ptRequestListEntityListId') {
                        switch (field) {
                          case 'productListsAllEnabled':
                          case 'brandsAllEnabled':
                          case 'isLocked':
                          case 'isExperimentConfigurationsEnabled':
                            if (value === '0' || value === '1') {
                              draft[field] = value === '1';
                            } else {
                              throw new Error("Value from signalR is not '0' or '1'!");
                            }
                            break;
                          case 'regionFlowTypeId':
                            if (value && isEnumValue(RegionFlowType, value)) {
                              draft[field] = value;
                            } else {
                              throw new Error('Value from signalR is not regionFlowType!');
                            }
                            break;
                          case 'skuListConfigurationFlagIds':
                            draft[field] = transformStringToSkuListConfigurationFlagIds(
                              value ?? '',
                            );
                            break;
                          case 'ptRequestListEntityListId': {
                            // The signal carries only the id; the row wants the entity list itself.
                            draft.ptRequestListEntityList = value
                              ? loadedRequestLists?.find(el => el.entityListId === parseInt(value))
                              : undefined;
                            break;
                          }
                        }
                      }
                    };

                    const requestListUpdate = signal.updates.find(
                      u => u.fieldName === 'ptRequestListEntityListId' && u.value !== undefined,
                    );
                    // A request-list write - an explicit value (a list or null) or the legacy
                    // empty-updates clear - takes the next generation; an omitted value takes none.
                    const writesRequestList =
                      requestListUpdate !== undefined || signal.updates.length === 0;
                    const generation = writesRequestList
                      ? ++requestListGeneration
                      : requestListGeneration;

                    // Everything but the request list, now, in the order it arrived.
                    api.updateCachedData(draft => {
                      if (draft) {
                        // cache entry may be undefined for manual and merged calculations
                        //TODO: very bad temporary request list configuration change!
                        // - currently when we remove entity list, PATCH signalr is with empty update array
                        if (signal.updates.length === 0) {
                          draft.ptRequestListEntityList = undefined;
                        }

                        signal.updates
                          .filter(u => u.fieldName !== 'ptRequestListEntityListId')
                          .forEach(u => applyField(draft, u.fieldName, u.value, undefined));
                      }
                    });

                    if (requestListUpdate === undefined) {
                      return;
                    }

                    let loadedRequestLists: EntityListResponse[] | undefined = [];
                    if (requestListUpdate.value) {
                      loadedRequestLists = (
                        await api.dispatch(
                          apiEntityLists.endpoints.getEntityLists.initiate(
                            getDefaultEntityListsParams(EntityListTypeName.Request),
                          ),
                        )
                      ).data;
                    }

                    // A newer request-list notification arrived while this one was loading: its
                    // request-list value is stale and is dropped.
                    if (generation !== requestListGeneration) {
                      return;
                    }

                    api.updateCachedData(draft => {
                      if (draft) {
                        applyField(
                          draft,
                          'ptRequestListEntityListId',
                          requestListUpdate.value,
                          loadedRequestLists,
                        );
                      }
                    });
                  } else if (signal.operationType === ApiOperationType.Delete) {
                    api.updateCachedData(draft => {
                      if (draft) {
                        // cache entry may be undefined for manual and merged calculations}
                        signal.updates.forEach(update => {
                          const field = update.fieldName;
                          const value = update.value;
                          if (field in draft && value) {
                            switch (field) {
                              case 'skuListConfigurationFlagIds':
                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- field is in draft, should exist
                                draft[field]![value] = undefined;
                                break;
                            }
                          }
                        });
                      }
                    });
                  }
                }
              })();
            };

            signalrClient.on('TemplateConfigurationChanged', listener);

            api.cacheEntryRemoved.then(() => {
              signalrClient.off('TemplateConfigurationChanged', listener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),

      updateCalculationConfiguration: builder.mutation<
        CalculationConfigurationResponseTransformed,
        UpdateCalculationConfigurationPayload
      >({
        // The shared configuration feeds every area report.
        invalidatesTags: (result, error, { templateOrCalculation }) => [
          validationProblemsTag(templateOrCalculation),
        ],
        queryFn: async payload => {
          const { data } = await TemplatesCollection.patchTemplateCalculationConfiguration(
            payload.templateOrCalculation.id,
            payload.request,
          );

          const skuListConfigurationFlagIds = transformStringToSkuListConfigurationFlagIds(
            data.data.skuListConfigurationFlagIds ?? '',
          );

          return { data: { ...data.data, skuListConfigurationFlagIds } };
        },
        onQueryStarted(payload, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            apiGlobalConfiguration.util.updateQueryData(
              'getCalculationConfiguration',
              payload.templateOrCalculation,
              draft => {
                if (draft) {
                  if ('skuListConfigurationFlagIds' in payload.request) {
                    const config = payload.request.skuListConfigurationFlagIds;
                    if (config) {
                      Object.assign(draft, {
                        skuListConfigurationFlagIds: {
                          ...draft.skuListConfigurationFlagIds,
                          [config]: true,
                        },
                      });
                    }
                  } else {
                    Object.assign(draft, payload.request);
                  }
                }
              },
            ),
          );
          queryFulfilled.catch(patchResult.undo);
        },
      }),

      // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- void type
      deleteCalculationConfiguration: builder.mutation<void, DeleteCalculationConfigurationPayload>(
        {
          invalidatesTags: (result, error, { templateOrCalculation }) => [
            validationProblemsTag(templateOrCalculation),
          ],
          queryFn: async payload => {
            await TemplatesCollection.calculationConfigurationDelete(
              payload.templateOrCalculation.id,
              payload.request,
            );
            return { data: undefined };
          },
          onQueryStarted(payload, { dispatch, queryFulfilled }) {
            const patchResult = dispatch(
              apiGlobalConfiguration.util.updateQueryData(
                'getCalculationConfiguration',
                payload.templateOrCalculation,
                draft => {
                  if (draft) {
                    if ('skuListConfigurationFlagIds' in payload.request) {
                      const config = payload.request.skuListConfigurationFlagIds;
                      if (config) {
                        Object.assign(draft, {
                          skuListConfigurationFlagIds: {
                            ...draft.skuListConfigurationFlagIds,
                            [config]: undefined,
                          },
                        });
                      }
                    }
                  }
                },
              ),
            );
            queryFulfilled.catch(patchResult.undo);
          },
        },
      ),
    }),
  });

export const {
  useGetCalculationConfigurationQuery,
  useUpdateCalculationConfigurationMutation,
  useDeleteCalculationConfigurationMutation,
} = apiGlobalConfiguration;
